import ImportEmployeeErrorModal from '@/components/modal/ImportEmployeeErrorModal'
import ImportEmployeeResultModal from '@/components/modal/ImportEmployeeResultModal'
import UploadFile from '@/components/UploadFile'
import JSZip from 'jszip'

export default {
    components: {
        UploadFile,
        ImportEmployeeResultModal,
        ImportEmployeeErrorModal
    },

    data () {
        return {
            isDrag: false,
            file: null,
            isUploading: false,
            uploadFiles: [],
            importResult: {
                title: '',
                content: null
            },
            importError: {},
            importImageResult: {
                total: 0,
                success: 0,
                error: {
                    total: 0,
                    employeeIdNotFound: 0,
                    employeeIdDuplicate: 0,
                    invalidImageType: 0,
                    invalidImageSize: 0,
                    importEmployeeSuccess: 0,
                    imageUploadFailed: 0,
                    unableToImageDevice: 0,
                    unableToServer: 0
                },
                validationInfo: {
                    employeeIdNotFound: [],
                    employeeIdDuplicate: [],
                    invalidImageType: [],
                    invalidImageSize: []
                },
                imageUploadInfo: [],
                employeeIds: []
            }
        }
    },

    computed: {
        classDragged () {
            if (this.isDrag) {
                return 'big-image'
            }
            return ''
        },

        onDrag () {
            if (this.isDrag) {
                return 'on-drag'
            }
            return ''
        }
    },

    methods: {
        dragOver () {
            this.isDrag = true
        },

        dragLeave () {
            this.isDrag = false
        },

        selectFile () {
            this.$refs.browseFile.click()
        },

        async onSelectFile (event) {
            const fileList = event.target.files

            if (this.isUploading === true) {
                this.$store.dispatch('setErrorModalShow', true)
                this.$store.dispatch('setErrorModalMsg', this.$vuetify.lang.t('$vuetify.employeeImportList.uploadOneFile'))
            }

            if (fileList[0]) {
                this.file = fileList[0]
                await this.uploadFile()
            }
        },

        getBase64ImageSize (imageBase64) {
            const diff = imageBase64.endsWith('==') ? 2 : 1
            return (imageBase64.length * (3 / 4)) - diff
        },

        isNumericString (str) {
            if (typeof str !== 'string') {
                return false
            }
            const v = Math.floor(Number(str))
            return v !== Infinity && String(v) === str && v >= 0
        },

        validateImage (imageName, imageBase64) {
            const fileParts = imageName.split('.')
            const employeeId = fileParts[0]
            let mime = fileParts[fileParts.length - 1]

            // validate file name
            if (fileParts.length > 2 || !this.isNumericString(employeeId)) {
                return 'EMPLOYEE_ID_NOT_FOUND'
            }

            // validate file type
            if (!['jpeg', 'jpg'].includes(mime)) {
                return 'INVALID_IMAGE_TYPE'
            }
            mime = 'jpeg'

            // validate file size must not exceed 200KB
            if (this.getBase64ImageSize(imageBase64) > (200 * 1024)) {
                return 'INVALID_IMAGE_SIZE'
            }

            // validate duplicate employee id
            if (this.importImageResult.employeeIds.includes(employeeId)) {
                return 'EMPLOYEE_ID_DUPLICATE'
            }
            this.importImageResult.employeeIds.push(employeeId)

            return { employee_id: employeeId, image: `data:image/${mime};base64, ${imageBase64}` }
        },

        async uploadImage (body) {
            const res = await this.axios.post('import/employee-face', body)
            if (res && res.data && res.data.status === 'ok') {
                return res.data.data
            }
            return {
                result: 'UNABLE_TO_SERVER',
                detail: {
                    employeeId: body.employee_id,
                    message: 'Internal server error'
                }
            }
        },

        updateResult (imageName, resultKey, detail) {
            switch (resultKey) {
                case 'EMPLOYEE_ID_NOT_FOUND':
                    this.importImageResult.error.total += 1
                    this.importImageResult.error.employeeIdNotFound += 1
                    this.importImageResult.validationInfo.employeeIdNotFound.push(imageName)
                    break
                case 'EMPLOYEE_ID_DUPLICATE':
                    this.importImageResult.error.total += 1
                    this.importImageResult.error.employeeIdDuplicate += 1
                    this.importImageResult.validationInfo.employeeIdDuplicate.push(imageName)
                    break
                case 'INVALID_IMAGE_TYPE':
                    this.importImageResult.error.total += 1
                    this.importImageResult.error.invalidImageType += 1
                    this.importImageResult.validationInfo.invalidImageType.push(imageName)
                    break
                case 'INVALID_IMAGE_SIZE':
                    this.importImageResult.error.total += 1
                    this.importImageResult.error.invalidImageSize += 1
                    this.importImageResult.validationInfo.invalidImageSize.push(imageName)
                    break
                case 'IMPORT_EMPLOYEE_SUCCESS':
                    this.importImageResult.success += 1
                    this.importImageResult.error.importEmployeeSuccess += 1
                    this.importImageResult.imageUploadInfo.push(detail)
                    break
                case 'IMAGE_UPLOAD_FAILED':
                    this.importImageResult.error.total += 1
                    this.importImageResult.error.imageUploadFailed += 1
                    this.importImageResult.imageUploadInfo.push(detail)
                    break
                case 'UNABLE_TO_IMAGE_DEVICE':
                    this.importImageResult.error.total += 1
                    this.importImageResult.error.unableToImageDevice += 1
                    break
                case 'UNABLE_TO_SERVER':
                    this.importImageResult.error.total += 1
                    this.importImageResult.error.unableToServer += 1
                    break
                default:
                    break
            }
        },

        clearResult () {
            this.$set(this, 'importImageResult', {
                total: 0,
                success: 0,
                error: {
                    total: 0,
                    employeeIdNotFound: 0,
                    employeeIdDuplicate: 0,
                    invalidImageType: 0,
                    invalidImageSize: 0,
                    importEmployeeSuccess: 0,
                    imageUploadFailed: 0,
                    unableToImageDevice: 0,
                    unableToServer: 0
                },
                validationInfo: {
                    employeeIdNotFound: [],
                    employeeIdDuplicate: [],
                    invalidImageType: [],
                    invalidImageSize: []
                },
                imageUploadInfo: [],
                employeeIds: []
            })
        },

        async uploadZipFile (file, progressFunc) {
            this.clearResult()
            const zip = await JSZip.loadAsync(file)
            const filenameList = Object.keys(zip.files)
            const fileCount = filenameList.length
            this.importImageResult.total = fileCount

            for (let i = 0; i < fileCount; i++) {
                const imageName = filenameList[i]
                const imageBase64 = await zip.files[imageName].async('base64')
                const result = this.validateImage(imageName, imageBase64)

                let resultKey = result
                let detail = null
                if (typeof result === 'object') {
                    const response = await this.uploadImage(result)
                    resultKey = response.result
                    detail = response.detail
                }
                this.updateResult(imageName, resultKey, detail)
                progressFunc(i + 1, fileCount)
            }

            const content = JSON.parse(JSON.stringify(this.importImageResult.error))
            content.total = fileCount
            content.unableToImageDevice = this.importImageResult.error.unableToImageDevice +
                this.importImageResult.error.unableToServer +
                this.importImageResult.error.imageUploadFailed
            content.imageUploadFailed = this.importImageResult.error.total
            delete content.unableToServer

            const logData = JSON.parse(JSON.stringify(this.importImageResult))
            delete logData.importEmployeeSuccess
            delete logData.employeeIds

            return { content, logData }
        }
    }
}
